import { DomainError } from '../errors/domain-error';
import { memberMeetsMinLevel } from '../commerce/community-gamification';
import { isCommunityModeratorRole } from './community-post.policy';

export const COMMUNITY_RESOURCE_TITLE_MAX_CHARS = 120;
export const COMMUNITY_RESOURCE_DESCRIPTION_MAX_CHARS = 500;

export type CommunityResourceKind = 'folder' | 'file' | 'link';

export function normalizeCommunityResourceTitle(title?: string | null): string {
  const trimmed = title?.trim();
  if (!trimmed) return '';
  return trimmed.slice(0, COMMUNITY_RESOURCE_TITLE_MAX_CHARS);
}

export function normalizeCommunityResourceDescription(description?: string | null): string | null {
  const trimmed = description?.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, COMMUNITY_RESOURCE_DESCRIPTION_MAX_CHARS);
}

export function validateCommunityResourcePayload(params: {
  kind: CommunityResourceKind;
  title?: string | null;
  linkUrl?: string | null;
  fileUrl?: string | null;
  fileKey?: string | null;
}): { ok: true; title: string } | { ok: false; message: string } {
  const title = normalizeCommunityResourceTitle(params.title);
  if (!title) {
    return { ok: false, message: 'Resource title is required' };
  }

  if (params.kind === 'link') {
    if (!params.linkUrl?.trim()) {
      return { ok: false, message: 'Link URL is required' };
    }
    try {
      const url = new URL(params.linkUrl.trim());
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return { ok: false, message: 'Link URL must use http or https' };
      }
    } catch {
      return { ok: false, message: 'Link URL is invalid' };
    }
  }

  if (params.kind === 'file') {
    if (!params.fileUrl?.trim() || !params.fileKey?.trim()) {
      return { ok: false, message: 'File upload must be confirmed before saving' };
    }
  }

  return { ok: true, title };
}

export function memberMeetsResourceTierAccess(params: {
  memberTierAccessRank?: number | null;
  requiredTierAccessRank?: number | null;
}): boolean {
  if (params.requiredTierAccessRank == null) return true;
  const memberRank = params.memberTierAccessRank ?? 0;
  return memberRank >= params.requiredTierAccessRank;
}

export function resolveCommunityResourceAccess(params: {
  kind: CommunityResourceKind;
  minLevel?: number | null;
  minTierAccessRank?: number | null;
  gamificationEnabled: boolean;
  memberLevel: number;
  memberTierAccessRank?: number | null;
  membershipRole?: string | null;
}): {
  locked: boolean;
  unlockLevelName?: string;
  unlockTierName?: string;
  canOpen: boolean;
} {
  if (params.membershipRole && isCommunityModeratorRole(params.membershipRole)) {
    return { locked: false, canOpen: true };
  }

  const levelLocked =
    params.gamificationEnabled &&
    params.minLevel != null &&
    params.minLevel > 0 &&
    !memberMeetsMinLevel(params.memberLevel, params.minLevel);

  const tierLocked = !memberMeetsResourceTierAccess({
    memberTierAccessRank: params.memberTierAccessRank,
    requiredTierAccessRank: params.minTierAccessRank,
  });

  const locked = levelLocked || tierLocked;
  const canOpen = !locked || params.kind === 'folder';

  return {
    locked,
    canOpen,
    unlockLevelName: levelLocked ? `Level ${params.minLevel}` : undefined,
    unlockTierName: tierLocked ? 'Higher membership tier' : undefined,
  };
}

export function assertCommunityResourceParent(params: {
  parentKind?: CommunityResourceKind | null;
}): void {
  if (params.parentKind && params.parentKind !== 'folder') {
    throw new DomainError('Resources can only be nested inside folders');
  }
}
